// @flow

// Note: The main reason this component exists is because the default
// Apollo cache (InMemoryCache) can't handle the amount of data (without
// massively affecting performance) returned by the Polaner API so we are
// gonna be handling the request for getting the module's data outside
// Apollo primarily to keep it outside of the cache.

import * as React from 'react';
import fetch from 'unfetch';
import { execute, makePromise } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { Query } from 'react-apollo';
import deepEqual from 'deep-equal';

import graphqlConfig from '../config/graphql';

const link = new HttpLink({
  ...graphqlConfig,
  fetch,
});

const fetcher = (params: { query: any, variables: any }) => {
  return makePromise(
    execute(link, {
      ...params,
    }),
  );
};

type ChildProps = {
  data: { [prop: any]: any },
  loading: boolean,
  refetching: boolean,
  refetched: boolean,
  error: any,
};

type CustomQueryProps = {
  fetcher?: typeof fetcher,
  variables: { [prop: any]: any },
  query: string,
  children: (
    ChildProps & {
      refetch: (?boolean) => mixed,
    },
  ) => React.Node,
  flush?: boolean,
};

type State = ChildProps & {};

class CustomQuery extends React.Component<CustomQueryProps, State> {
  static defaultProps = {
    fetcher,
  };

  state = {
    data: {},
    loading: false,
    refetching: false,
    refetched: false,
    error: null,
  };

  oldDerivedStateFromProps(nextProps: CustomQueryProps) {
    if (nextProps.flush === true) {
      this.setState({
        data: {},
      });
    }

    return null;
  }

  fetchData = async (refetching: boolean = false) => {
    this.setState({
      loading: true,
      refetching,
      refetched: false,
    });

    try {
      // $FlowFixMe
      const { data } = await this.props.fetcher({
        query: this.props.query,
        variables: this.props.variables,
      });

      this.setState({
        data,
        loading: false,
        refetching: false,
        refetched: refetching,
      });
    } catch (e) {
      this.setState({
        loading: false,
        refetching: false,
        error: e,
      });
    }
  };

  refetch = () => this.fetchData(true);

  componentDidMount = async () => {
    this.oldDerivedStateFromProps(this.props);
    this.fetchData();
  };

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props)
    }
    if (!deepEqual(this.props.variables, prevProps.variables)) {
      this.refetch();
    }
  }

  render() {
    return this.props.children({
      data: this.state.data,
      loading: this.state.loading,
      refetching: this.state.refetching,
      refetched: this.state.refetched,
      error: this.state.error,
      refetch: this.refetch,
    });
  }
}

type TrackNetworkStatusProps = {
  // eslint-disable-next-line
  networkStatus: number,
  // eslint-disable-next-line
  loading: boolean,
  children: ({
    refetched: boolean,
  }) => React.Node,
};

type TrackNetworkStatusState = {
  refetchStatus: string,
};

class TrackNetworkStatus extends React.Component<
  TrackNetworkStatusProps,
  TrackNetworkStatusState,
> {
  state = {
    refetchStatus: '',
  };

  oldDerivedStateFromProps(
    props: TrackNetworkStatusProps,
    state: TrackNetworkStatusState,
  ) {
    // 4 means 'refetching'
    if (props.networkStatus === 4 && props.loading) {
      return this.setState({
        refetchStatus: 'refetching',
      });
    } else if (
      props.networkStatus !== 4 &&
      state.refetchStatus === 'refetching'
    ) {
      return this.setState({
        refetchStatus: 'refetched',
      });
    }

    return null;
  }

  componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state);
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      this.oldDerivedStateFromProps(this.props, this.state);
    }
  }

  render() {
    return this.props.children({
      refetched: this.state.refetchStatus === 'refetched',
    });
  }
}

type Props = {
  cache?: boolean,
};

export default class ModuleQuery extends React.Component<
  Props & CustomQueryProps,
> {
  render() {
    if (this.props.cache) {
      return (
        <Query
          query={this.props.query}
          variables={this.props.variables}
          notifyOnNetworkStatusChange
        >
          {({ data, loading, error, refetch, networkStatus }: any) => {
            const refetching = networkStatus === 4;

            return (
              <TrackNetworkStatus
                networkStatus={networkStatus}
                loading={loading}
              >
                {({ refetched }) =>
                  this.props.children({
                    data,
                    loading,
                    refetching,
                    refetched,
                    error,
                    refetch,
                  })
                }
              </TrackNetworkStatus>
            );
          }}
        </Query>
      );
    }

    return <CustomQuery {...this.props} />;
  }
}
